import React, { useState } from 'react';
import { format } from 'date-fns';
import { Popover } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseDayKey } from '../../CustomBlocks/slotBooking/slotUtils';
import { CloseIcon, PlusIcon } from '../../shared/Icons';
import { SettingsGroup, SettingsRow } from './settingsUi';
import { TextButton } from './SlotBookingEditor.styles';

interface BlockedDatesEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
}

const formatDay = (key: string) => {
  const date = parseDayKey(key);
  return date ? format(date, 'EEE, d MMM yyyy') : key;
};

const BlockedDatesEditor: React.FC<BlockedDatesEditorProps> = ({ value, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const add = (date: Date | null) => {
    setAnchor(null);
    if (!date) return;
    const key = format(date, 'yyyy-MM-dd');
    if (!value.includes(key)) onChange([...value, key].sort());
  };

  return (
    <SettingsGroup
      title="Blocked dates"
      footer="No slots are offered on these dates, e.g. holidays or time off."
    >
      {value.map((date) => (
        <SettingsRow key={date} label={formatDay(date)}>
          <TextButton
            type="button"
            $tone="muted"
            aria-label={`Remove ${formatDay(date)}`}
            onClick={() => onChange(value.filter((d) => d !== date))}
          >
            <CloseIcon size={14} />
          </TextButton>
        </SettingsRow>
      ))}
      <SettingsRow label={value.length ? 'Block another date' : 'Block a date'}>
        <TextButton type="button" onClick={(e) => setAnchor(e.currentTarget)}>
          <PlusIcon size={13} />
          Add date
        </TextButton>
      </SettingsRow>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            value={null}
            onChange={add}
            disablePast
            views={['day']}
            shouldDisableDate={(day) => value.includes(format(day, 'yyyy-MM-dd'))}
          />
        </LocalizationProvider>
      </Popover>
    </SettingsGroup>
  );
};

export default BlockedDatesEditor;
